import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Clock, CheckCircle, Lock, CreditCard, ShieldCheck, LogOut } from 'lucide-react';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { createPortal } from 'react-dom';

/** Refresco cada 5 minutos mientras la sesión está activa. */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Rutas donde el wall NO debe aparecer aunque la suscripción esté vencida.
 * - /billing/** → el usuario tiene que poder ver y contratar planes
 * - /billing/success → confirmación post-pago
 * - /billing/cancel → cancelación de pago
 */
const WALL_EXEMPT_PATHS = ['/billing', '/billing/success', '/billing/cancel'];

/* ─────────────────────────────────────────────────────────────
   SubscriptionWall — overlay bloqueante full-screen.

   Montado vía React Portal en document.body para garantizar que
   cubre sidebar + topbar + content sin conflictos de z-index.
   No se puede cerrar; las únicas acciones son:
     · Ir a Suscripción (owner)         → navega a /billing sin wall
     · Cerrar sesión (owner + empleado) → logout y redirige a /login
───────────────────────────────────────────────────────────── */

interface SubscriptionWallProps {
  onSubscribe: () => void;
  onLogout: () => void;
}

const SubscriptionWall: React.FC<SubscriptionWallProps> = ({ onSubscribe, onLogout }) => {
  const { isOwner, user } = useAuth();
  const owner = isOwner();

  // Bloquear scroll del body mientras el wall está activo
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const wall = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Suscripción requerida"
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999, isolation: 'isolate' }}
    >
      {/* Backdrop — blur total sobre todo el viewport */}
      <div
        className="absolute inset-0"
        style={{
          backdropFilter: 'blur(18px) saturate(0.6) brightness(0.75)',
          WebkitBackdropFilter: 'blur(18px) saturate(0.6) brightness(0.75)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        aria-hidden="true"
      />

      {/* Card */}
      <div className="relative w-full max-w-sm mx-auto">
        {/* Glow ambiental */}
        <div
          className="absolute -inset-6 rounded-3xl opacity-25 blur-2xl pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, hsl(var(--primary)), transparent 70%)',
          }}
          aria-hidden="true"
        />

        <div className="relative rounded-2xl border border-border/50 bg-card shadow-2xl overflow-hidden">
          {/* Barra de acento superior */}
          <div
            className="h-px w-full"
            style={{
              background: 'linear-gradient(90deg, transparent, hsl(var(--primary)), transparent)',
            }}
          />

          <div className="px-7 pt-7 pb-6 flex flex-col items-center text-center gap-5">

            {/* Icono */}
            <div className="relative">
              <div
                className="flex items-center justify-center w-[60px] h-[60px] rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--primary)/0.18), hsl(var(--primary)/0.06))',
                  border: '1px solid hsl(var(--primary)/0.2)',
                }}
              >
                <Lock className="w-6 h-6" style={{ color: 'hsl(var(--primary))' }} />
              </div>
              {/* Pulso de alerta */}
              <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500 border-2 border-card" />
              </span>
            </div>

            {/* Títulos */}
            <div className="space-y-2">
              <h2 className="text-[17px] font-bold text-foreground leading-snug">
                {owner ? 'Tu período de prueba finalizó' : 'Acceso temporalmente suspendido'}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {owner
                  ? 'Elegí un plan para seguir usando PruneStone sin interrupciones. La activación es inmediata.'
                  : <>
                      El trial de{' '}
                      <span className="font-medium text-foreground">
                        {user?.name ?? 'tu empresa'}
                      </span>{' '}
                      ha finalizado. Contactá al administrador para reactivar el acceso.
                    </>
                }
              </p>
            </div>

            {/* Acciones */}
            <div className="w-full space-y-2.5">
              {/* CTA principal — solo owner */}
              {owner && (
                <Button
                  id="subscription-wall-cta"
                  className="w-full h-10 text-sm font-semibold gap-2"
                  style={{
                    background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary)/0.85))',
                    boxShadow: '0 4px 20px hsl(var(--primary)/0.3)',
                  }}
                  onClick={onSubscribe}
                >
                  <CreditCard className="w-4 h-4" />
                  Ver planes de suscripción
                </Button>
              )}

              {/* Empleado: aviso */}
              {!owner && (
                <div className="w-full rounded-xl border border-border bg-muted/40 px-4 py-3">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground text-left leading-relaxed">
                      Solo el administrador puede activar o renovar la suscripción.
                    </p>
                  </div>
                </div>
              )}

              {/* Cerrar sesión — siempre visible */}
              <Button
                id="subscription-wall-logout"
                variant="outline"
                className="w-full h-10 text-sm gap-2 text-muted-foreground hover:text-foreground"
                onClick={onLogout}
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </Button>
            </div>

            {/* Trust badge — solo owner */}
            {owner && (
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
                <ShieldCheck className="w-3 h-3" />
                <span>Pago seguro · Stripe · Cancelá cuando quieras</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(wall, document.body);
};

/* ─────────────────────────────────────────────────────────────
   StatusPill — badge compacto en el topbar.
   shrink-0 + tamaño fijo → nunca rompe el flex del header.
───────────────────────────────────────────────────────────── */

interface StatusPillProps {
  status: SubscriptionStatus;
  onClick?: () => void;
}

const StatusPill: React.FC<StatusPillProps> = ({ status, onClick }) => {
  // No mostrar nada si ACTIVE con holgura
  if (status.status === 'ACTIVE' && status.daysRemaining > 14) return null;

  const urgent = status.status === 'EXPIRED' || (status.status === 'TRIAL' && status.daysRemaining <= 2);

  type PillConfig = {
    icon: React.ReactElement;
    label: string;
    pill: string;
  };

  const configs: Record<string, PillConfig> = {
    EXPIRED: {
      icon: <Lock className="w-3 h-3 shrink-0" />,
      label: 'Vencida',
      pill: 'bg-red-500/10 text-red-500 border-red-500/25 hover:bg-red-500/20',
    },
    TRIAL_URGENT: {
      icon: <AlertTriangle className="w-3 h-3 shrink-0" />,
      label: status.daysRemaining <= 0 ? 'Vence hoy' : `${status.daysRemaining}d`,
      pill: 'bg-orange-500/10 text-orange-500 border-orange-500/25 hover:bg-orange-500/20',
    },
    TRIAL: {
      icon: <Clock className="w-3 h-3 shrink-0" />,
      label: `Trial · ${status.daysRemaining}d`,
      pill: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/25 hover:bg-yellow-500/20',
    },
    ACTIVE: {
      icon: <CheckCircle className="w-3 h-3 shrink-0" />,
      label: `${status.daysRemaining}d`,
      pill: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/25',
    },
  };

  const key =
    status.status === 'EXPIRED' ? 'EXPIRED' :
    status.status === 'TRIAL' && status.daysRemaining <= 2 ? 'TRIAL_URGENT' :
    status.status === 'TRIAL' ? 'TRIAL' : 'ACTIVE';

  const config = configs[key];

  return (
    <button
      id="subscription-status-pill"
      onClick={onClick}
      disabled={!onClick}
      className={[
        'shrink-0 flex items-center gap-1.5',
        'h-7 px-2.5 rounded-full border',
        'text-[11px] font-semibold leading-none whitespace-nowrap',
        'transition-colors duration-150 outline-none',
        'focus-visible:ring-2 focus-visible:ring-primary/40',
        config.pill,
        onClick ? 'cursor-pointer' : 'cursor-default pointer-events-none',
      ].join(' ')}
    >
      {urgent && (
        <span className="relative flex h-1.5 w-1.5 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
        </span>
      )}
      {config.icon}
      <span className="hidden sm:inline">{config.label}</span>
    </button>
  );
};

/* ─────────────────────────────────────────────────────────────
   SubscriptionBadge — componente raíz exportado.
───────────────────────────────────────────────────────────── */

export const SubscriptionBadge: React.FC = () => {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner, logout } = useAuth();
  const owner = isOwner();

  const fetchStatus = useCallback(async () => {
    try {
      const data = await subscriptionService.getStatus();
      setStatus(data);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  if (!status) return null;

  const handleSubscribe = () => navigate('/billing');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // El wall NO aparece en rutas de billing (el usuario tiene que poder suscribirse)
  const isOnExemptPath = WALL_EXEMPT_PATHS.some(p => location.pathname.startsWith(p));
  const showWall = status.status === 'EXPIRED' && !isOnExemptPath;

  return (
    <>
      {/* Pill compacto en el topbar */}
      <StatusPill
        status={status}
        onClick={owner ? handleSubscribe : undefined}
      />

      {/* Wall bloqueante montado via Portal */}
      {showWall && (
        <SubscriptionWall
          onSubscribe={handleSubscribe}
          onLogout={handleLogout}
        />
      )}
    </>
  );
};
